/**
 * 表紙
 *
 * これまで、開くといきなり40マスの盤面が出ていた。
 * 何の遊びなのか、いくつ遊びがあるのか、どれから触ればいいのかが
 * どこにも書かれていなかった。**最初の画面は遊びの入口である。**
 *
 * 【並べる順に意味を持たせる】
 * 双六が本体、合戦はその一部分だけを取り出したもの、
 * ポーカーは**別のゲーム**。この関係を字で書いておかないと、
 * 「軍配勝負」と「テキサスホールデム」が同じものに見える。
 *
 * 【断りを最初に置く】
 * 実際の金銭を一切扱わないことは、遊ぶ前に読める場所に書く。
 * 賭け事を扱う以上、footer の小さい字では足りない。
 */

import { savedAt } from './board/save';
import './title.css';

interface Entry {
  readonly hash: string;
  readonly name: string;
  readonly reading: string;
  readonly lead: string;
  readonly minutes: string;
  readonly main?: boolean;
}

const ENTRIES: readonly Entry[] = [
  {
    hash: 'board',
    name: '天下双六',
    reading: 'てんかすごろく',
    lead: '40マスの環を回り、国を切り取る。着いた先が他家の国なら合戦になる。',
    minutes: '30分ほど',
    main: true,
  },
  {
    hash: 'battle',
    name: '軍配勝負',
    reading: 'ぐんばいしょうぶ',
    lead: '双六の合戦だけを取り出したもの。5兵種65枚の軍札で陣形を組む。',
    minutes: '3分ほど',
  },
  {
    hash: 'holdem',
    name: 'テキサスホールデム',
    reading: '',
    lead: 'トランプ52枚の標準ルール。天下双六とは別のゲームで、山札も役も独立している。',
    minutes: '10分ほど',
  },
  {
    hash: 'room',
    name: '対戦',
    reading: '',
    lead: '同じ端末で回して遊ぶか、合言葉を配って離れた相手と遊ぶ。観るだけでも入れる。',
    minutes: '',
  },
  {
    hash: 'warlords',
    name: '武将を見る',
    reading: '',
    lead: '八人の大名。能力と、その裏にある史実を読む。',
    minutes: '',
  },
  {
    hash: 'codex',
    name: '図鑑',
    reading: 'ずかん',
    lead: '遊んでいるうちに溜まる。出会った国・武将・出来事を読み返す。',
    minutes: '',
  },
  {
    hash: 'mypage',
    name: '控え',
    reading: 'ひかえ',
    lead: 'これまでの戦績と設定。記録はこの端末の中だけに置いてある。',
    minutes: '',
  },
];

const dateText = (at: number): string => {
  const d = new Date(at);
  return `${d.getMonth() + 1}月${d.getDate()}日 ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
};

export function Title({ onGo }: { onGo: (hash: string) => void }): React.JSX.Element {
  const saved = savedAt();

  return (
    <div className="title-wrap">
      <header className="title-head">
        <div className="eyebrow">戦国 双六</div>
        <h1>
          天下双六
          <span className="yomi">てんかすごろく</span>
        </h1>
        <p className="lede">
          応仁の乱から関ヶ原まで、五つの章を進む。
          <strong>章が変わると史実がルールそのものを書き換える</strong>
          — 鉄砲が伝来すれば軍札に鉄砲が加わり、刀狩が出れば僧兵が減る。
          読んで覚えるのではなく、山札の中身の変化として時代を体感する遊びである。
        </p>
      </header>

      {saved != null ? (
        <button type="button" className="resume" onClick={() => onGo('board')}>
          <span className="resume-label">中断した局を続ける</span>
          <span className="resume-at">{dateText(saved)} に中断</span>
        </button>
      ) : null}

      <ul className="entries">
        {ENTRIES.map((e) => (
          <li key={e.hash} className={e.main ? 'main' : ''}>
            <button type="button" onClick={() => onGo(e.hash)}>
              <span className="ent-name">
                {e.name}
                {e.reading ? <i>{e.reading}</i> : null}
              </span>
              <span className="ent-lead">{e.lead}</span>
              {e.minutes ? <span className="ent-min">{e.minutes}</span> : null}
            </button>
          </li>
        ))}
      </ul>

      <section className="notice">
        <h2>お断り</h2>
        <p>
          <strong>実際の金銭は一切扱わない。</strong>
          賭けているのは盤上の兵と銭だけで、購入も換金もない。課金・ガチャの類も置かない。
        </p>
        <p>
          賭場・借銭・見物は<strong>まとめて出さないようにできる</strong>（双六の画面の「学校モード」）。
          合戦の賭けは軍配勝負そのものなので、そちらは残る。
        </p>
        <p className="small">
          史実の記述には、通説と異なる説があるものが多くある。
          そういう箇所には必ず「ただし」を添えてある。<strong>通説だけを教える遊びにはしない。</strong>
        </p>
      </section>
    </div>
  );
}
